<template>
<div data-component="VideoTrigger.vue" @click.stop>
	<!-- Infoboks -->
	<div class="flex gap-4 py-3 border rounded-lg border-info bg-info-design-50" :class="mobileSize ? 'flex-col px-3' : 'items-center px-6'">
		<div class="flex gap-4" :class="mobileSize ? '' : 'grow'">
			<BaseIcon set="video" name="trigger-icon" :size="mobileSize ? 1.25 : 1.5" class="text-info"/>
			<div>
				<b v-if="header" :class="mobileSize ? 'block text-sm' : 'text-base'" v-text="header + ' '"/>
				<span v-if="body" :class="mobileSize ? 'text-xs' : 'text-base'" v-text="body"/>
			</div>
		</div>
		<button class="gap-2 btn btn-info btn-small" :class="mobileSize ? 'grow' : ''" @click.stop.prevent="openPlayer()">
			<BaseIcon set="video" name="video-player-movie" :size="1.25"/> Zobacz
		</button>
	</div>

	<!-- Odtwarzacz -->
	<div ref="player">
		<div
			v-if="isPlayerVisible"
			class="fixed bottom-0 right-0 flex flex-col bg-white a-loaded z-2048"
			:class="isFullscreen ? 'top-0 left-0' : 'm-4 w-80 border border-info rounded-lg shadow-large'"
			@mousemove="resetControlsVisibility"
		>
			<div
				class="flex items-center gap-2 px-4 py-5 transition-opacity"
				:class="{
					'opacity-0': hideControls && isFullscreenEnabled,
					'absolute inset-x-0 top-0 bg-black/50 text-white backdrop:backdrop-blur z-4' : isFullscreenEnabled
				}"
			>
				<BaseIcon set="video" name="video-player-movie" :size="1.5" :class="isFullscreenEnabled ? 'text-current' : 'text-neutral-700'"/>
				<div class="text-base font-bold break-all line-clamp-1 grow" v-text="videos[index].title"/>
				<button class="p-2" @click="closePlayer">
					<BaseIcon name="close" :size="0.5"/>
				</button>
			</div>

			<div class="relative grow" :class="isFullscreen ? 'max-h-full' : 'px-2 pb-2'">
				<VideoPlayer
					:class="isFullscreen ? '!absolute inset-0 bg-neutral-900 flex items-center' : 'rounded-lg overflow-hidden'"
					:filename="videoFilename"
					:oggFilename="oggVideoFilename"
					:hideControls="hideControls && isFullscreenEnabled"
					:autoplay="autoplay"
					:displayAdditionalControls="videos.length > 1"
					@progress="onVideoProgress"
					@toggle-fullscreen="onToggleFullscreen"
					@playback-change="isPaused = $event"
					@switch-video="switchVideo($event)"
				/>
			</div>

			<template v-if="videos.length > 1">
				<div v-if="!isFullscreenEnabled" class="flex justify-center h-12 gap-2 mx-3 mb-2 font-bold">
					<button v-if="index" class="text-secondary" @click="switchVideo(-1)">
						<BaseIcon name="arrow-left" :size="1.25"/>
					</button>
					<button class="justify-end gap-2 ml-auto text-right text-secondary grow" @click="switchVideo(1)">
						<div class="line-clamp line-clamp-2" v-text="`Zobacz kolejny: ${nextVideoTitle}`"/>
						<BaseIcon name="arrow-right" :size="1.25"/>
					</button>
				</div>
				<div 
					v-else-if="isPaused && isVideoFinished"
					class="absolute right-0 flex items-center gap-2 p-4 mx-4 mb-4 text-white rounded-lg cursor-pointer w-72 bg-black/50 bottom-16 a-loaded"
					@click="switchVideo(1)"
				>
					<BaseIcon set="video" name="video-player-movie" :size="1.5"/>
					<div class="text-xs font-bold text-right line-clamp line-clamp-2" v-text="`Zobacz kolejny: ${nextVideoTitle}`"/>
					<BaseIcon name="arrow-right" :size="1.25"/>
				</div>
			</template>
		</div>
	</div>
</div>
</template>

<script lang="ts">
import { defineAsyncComponent } from 'vue';

/**
 * Komponent wyzwalający odtwarzanie filmu.
 */
export default {
	name: 'VideoTrigger',
	components: {
		VideoPlayer: defineAsyncComponent(() => import('./VideoPlayer.vue')),
	},
	props: {
		/**
		 * Lista filmów do odtworzenia z tytułem i nazwą pliku.
		 */
		videos: {
			type: Array as () => { title: string, name: string }[],
			required: true,
		},

		/**
		 * Miejsce w którym znajduje się infoboks.
		 */
		source: {
			type: String,
			required: false,
		},

		/**
		 * Nagłówek infoboksa i tytuł filmu.
		 */
		header: {
			type: String,
			required: true,
		},

		/**
		 * Treść infoboksa.
		 */
		body: {
			type: String,
			required: true,
		},

		/**
		 * Czy infoboks ma być w wersji mobilnej (przycisk pod tekstem)?
		 */
		mobileSize: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			/**
			 * Czy okienko odtwarzacza jest widoczne?
			 */
			isPlayerVisible: false,

			/**
			 * Czy odtwarzacz jest w trybie pełnoekranowym?
			 */
			isFullscreenEnabled: false,

			/**
			 * Referencja do opóźnienia ukrywającego kontrolki odtwarzacza.
			 */
			hideControlsTimeout: 0,

			/**
			 * Czy pokazywać kontrolki odtwarzania?
			 */
			hideControls: false,

			/**
			 * Czy odtworzyć film automatycznie?
			 */
			autoplay: true,

			/**
			 * Indeks aktualnie wyświetlanego filmu.
			 */
			index: 0,

			/**
			 * Czy filmik jest obecnie zatrzymany?
			 */
			isPaused: false,

			/**
			 * Czy filmik został obejrzany do końca?
			 */
			isVideoFinished: false,
		};
	},
	computed: {
		/**
		 * Czy odtwarzacz zajmuje całą powierzchnię ekranu (tj. komórka lub włączony pełny ekran)?
		 */
		isFullscreen() {
			return this.ScreenSize <= 1 || this.isFullscreenEnabled;
		},

		/**
		 * Referencja do elementu odtwarzacza.
		 */
		player() {
			return this.$refs.player as HTMLDivElement;
		},

		/**
		 * Ścieżka do pliku wideo.
		 */
		videoFilename() {
			const staticHost = document.location.origin.replace('www', 'static');
			return `${staticHost}/images/videos/customer-app/${this.currentVideoName}.mp4`;
		},

		/**
		 * Alternatywna ścieżka do pliku wideo.
		 */
		oggVideoFilename() {
			const staticHost = document.location.origin.replace('www', 'static');
			return `${staticHost}/images/videos/customer-app/${this.currentVideoName}.ogg`;
		},

		currentVideoName() {
			return this.videos[this.index].name;
		},

		nextVideoTitle() {
			return this.videos[(this.index + 1) % this.videos.length].title;
		},
	},
	mounted() {
		document.addEventListener('fullscreenchange', this.fullscreenEventListener);
		this.resetControlsVisibility();
		this.checkAutoplay();
	},
	unmounted() {
		document.removeEventListener('fullscreenchange', this.fullscreenEventListener);
	},
	methods: {
		switchVideo(direction: number) {
			if (!this.videos) { return; }
			this.index = (this.index + direction + this.videos.length) % this.videos.length;
			this.sendAnalytics(this.currentVideoName, 0);
			this.isVideoFinished = false;
		},
		checkAutoplay() {
			const param = new URLSearchParams(window.location.search).get('autoplay') === 'true';
			if (!param || window.videoAutoplayTriggered) { return; }
			this.autoplay = false;
			this.openPlayer(false);
			window.videoAutoplayTriggered = true;
		},
		/**
		 * Ukrywa pasek z kontrolkami po dwóch sekundach oraz pokazuje go w momencie, kiedy poruszymy myszką.
		 */
		resetControlsVisibility() {
			if (this.hideControlsTimeout) {
				clearTimeout(this.hideControlsTimeout);
			}

			window.setTimeout(() => {
				this.hideControls = false;
			}, 50);

			this.hideControlsTimeout = window.setTimeout(() => {
				this.hideControls = true;
			}, 2000);
		},

		/**
		 * Wyświetla odtwarzacz oraz wysyła informację analityczną o jego otwarciu.
		 */
		openPlayer(mobileFullscreen = true) {
			this.sendAnalytics(this.currentVideoName, 0);
			this.isPlayerVisible = true;

			// Na mobilce od razu włączamy tryb pełnoekranowy.
			if (mobileFullscreen && this.ScreenSize <= 1) {
				this.onToggleFullscreen();
			}
		},

		/**
		 * Zamyka odtwarzacz oraz wyłącza tryb pełnoekranowy.
		 */
		async closePlayer() {
			this.isPlayerVisible = false;
			this.isFullscreenEnabled = false;
			try {
				if (document.fullscreenElement) {
					await document.exitFullscreen();
				}
			} catch (err) {
				// Sypie błędem w momencie, kiedy próbujemy wyjść z trybu pełnoekranowego,
				// a nie jesteśmy w nim. Można zignorować.
			}
		},

		/**
		 * Wysyła informację analityczną o obejrzeniu kolejnej ćwiartki filmu.
		 */
		onVideoProgress(progress: number) {
			this.isVideoFinished = progress === 100;
			this.sendAnalytics(this.currentVideoName, progress);
		},

		/**
		 * Nasłuchiwanie na zmianę trybu pełnoekranowego.
		 */
		fullscreenEventListener() {
			this.isFullscreenEnabled = !!document.fullscreenElement;
		},

		/**
		 * Włącza lub wyłącza tryb pełnoekranowy.
		 */
		async onToggleFullscreen() {
			const { player } = this;
			if (document.fullscreenElement) {
				try {
					if (document.fullscreenElement) {
						await document.exitFullscreen();
					}
				} catch (err) {
					// Sypie błędem w momencie, kiedy próbujemy wyjść z trybu pełnoekranowego,
					// a nie jesteśmy w nim. Można zignorować.
				}
			} else {
				player.requestFullscreen({
					navigationUI: 'hide',
				});
			}
		},

		/**
		 * Wysyła informację analityczną do GA i bazy danych.
		 */
		sendAnalytics(videoName: string, progress: number) {
			if (progress > 0) {
				this.Analytics.Gtm('videoPlayer_progress', { videoName, progress, videoSource: this.source });
			} else {
				this.Analytics.Gtm('videoPlayer_open', { videoName, videoSource: this.source });
			}

			fetch('/myoferteo/konto-json/log-video-track.do', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					name: videoName,
					playbackTime: progress,
					source: this.source,
				}),
			});
		},
	},
};
</script>
